import DefaultVideoImage from '@/assets/images/DefaultVideoImage.jpg';
import startImage from '@/assets/images/RatingStar.svg';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { GetUserByIdQuery } from '@/Types/gql/graphql';
import { Play } from 'lucide-react';
import React, { useState } from 'react';
import MediaGallery from '../MediaGallery/MediaGallery';

const charLimit = 700;
interface TrainerDetailProps {
  trainerData: GetUserByIdQuery | null;
  loading: boolean;
}

const TrainerDetail: React.FC<TrainerDetailProps> = ({ trainerData, loading }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<number | null>(null);
  const shouldShowButton =
    trainerData?.getUserById?.about && trainerData?.getUserById?.about.length > charLimit;
  const toggleExpand = () => setIsExpanded(!isExpanded);
  const mediaData =
    trainerData?.getUserById?.medias?.filter(
      (media): media is NonNullable<typeof media> => media !== null,
    ) || [];
  return (
    <div className='text-center mt-5 flex flex-col items-start'>
      <div
        className='flex flex-col text-white p-[18px] rounded-[20px] w-full'
        style={{
          background:
            'linear-gradient(154.36deg, rgba(47, 47, 47, 0.44) 11.3%, rgba(47, 47, 47, 0.44) 95.38%)',
        }}
      >
        <div className='flex flex-row justify-between'>
          <div className='w-full flex flex-row'>
            <div className='rounded-full w-20 h-20 flex items-center justify-center bg-gray-600 text-white font-bold'>
              {loading ? (
                <Skeleton className='w-20 h-20 rounded-full bg-bgSecondary' />
              ) : trainerData?.getUserById?.profileImage ? (
                <img
                  src={trainerData?.getUserById?.profileImage}
                  alt={trainerData?.getUserById?.firstName || ''}
                  className='rounded-full w-20 h-20 object-cover'
                />
              ) : (
                <span className='text-3xl'>
                  {trainerData?.getUserById?.firstName?.charAt(0).toUpperCase() || ''}
                </span>
              )}
            </div>
            <div className='flex flex-col items-baseline justify-center ml-4 mx-4 gap-1'>
              {loading ? (
                <Skeleton className='w-40 h-6 bg-bgSecondary' />
              ) : (
                <span className='text-2xl font-semibold'>
                  {trainerData?.getUserById?.firstName || 'User not found'}
                </span>
              )}
              {loading ? (
                <Skeleton className='w-20 h-5 bg-bgSecondary' />
              ) : trainerData?.getUserById?._id ? (
                // typeof trainerData?.getUserById?.averageRating === 'number' &&
                // trainerData?.getUserById?.averageRating > 0 && (
                <div className='flex items-center justify-center gap-1'>
                  <img src={startImage} alt='star' className='w-4 h-4' />
                  <span>5</span>
                </div>
              ) : (
                //   {/* <span>{trainerData?.getUserById?.averageRating}</span> */}
                // )
                ''
              )}
            </div>
          </div>
        </div>
        <div className='my-5'>
          {loading ? (
            <div className='flex flex-col gap-3'>
              <Skeleton className='w-full h-4 bg-bgSecondary' />
              <Skeleton className='w-10/12 h-4 bg-bgSecondary' />
              <Skeleton className='w-1/6 h-4 bg-bgSecondary' />
            </div>
          ) : (
            <p className='text-[14px] font-normal text-justify'>
              {isExpanded
                ? trainerData?.getUserById?.about?.split('\n').map((line, index) => (
                    <React.Fragment key={index}>
                      {line}
                      <br />
                    </React.Fragment>
                  ))
                : trainerData?.getUserById?.about
                    ?.substring(0, charLimit)
                    .split('\n')
                    .map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
              {shouldShowButton && !isExpanded && '...'}
            </p>
          )}
          {shouldShowButton && !loading && (
            <div className='flex justify-end'>
              <button onClick={toggleExpand} className='text-blue-500 mt-2 text-[12px]'>
                {isExpanded ? 'Show Less' : 'Show More'}
              </button>
            </div>
          )}
        </div>
        <div className='flex flex-wrap gap-2 mt-4'>
          {loading
            ? Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} className='w-20 h-6 bg-bgSecondary' />
              ))
            : trainerData?.getUserById?.specializations?.map((item) => (
                <div
                  key={item?._id}
                  className='text-sm font-medium px-2 py-1 bg-[#343840] rounded-lg whitespace-nowrap'
                >
                  {item?.title}
                </div>
              ))}
        </div>
        <Separator className='my-5 bg-[#FFFFFF21] bg-bgSecondary' />
        <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3'>
          {loading
            ? Array.from({ length: 2 }).map((_, index) => (
                <Skeleton key={index} className='w-40 h-40 bg-bgSecondary' />
              ))
            : trainerData?.getUserById?.medias?.map((item, i) => (
                <div key={item?._id} className='flex items-center'>
                  {item?.type === 'IMAGE' ? (
                    <img
                      src={item?.path || ''}
                      alt='Media'
                      className='min-w-40 min-h-40 object-cover rounded-md aspect-square cursor-pointer'
                      onClick={() => setSelectedMedia(i)}
                    />
                  ) : item?.type === 'VIDEO' ? (
                    <div className='relative'>
                      <img
                        src={item?.thumbnail || DefaultVideoImage}
                        alt='video thumbnail'
                        className='min-w-40 min-h-40 object-cover rounded-md aspect-square cursor-pointer'
                        onClick={() => setSelectedMedia(i)}
                      />
                      <div
                        className='absolute top-0 left-0 w-full h-full flex items-center justify-center cursor-pointer'
                        onClick={() => setSelectedMedia(i)}
                      >
                        {item?.thumbnail ? (
                          <Play className='w-14 h-14' onClick={() => setSelectedMedia(i)} />
                        ) : (
                          ''
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
        </div>
      </div>
      {selectedMedia || selectedMedia === 0 ? (
        <MediaGallery
          selectedMedia={selectedMedia}
          mediaData={mediaData}
          setSelectedMedia={setSelectedMedia}
        />
      ) : (
        ''
      )}
    </div>
  );
};

export default TrainerDetail;
